import { Box, useTheme } from "@mui/material";
import { Button, Drawer, PlusIcon } from "components";
import {
  useCreateSsoConfig,
  useDeleteSsoConfig,
  useUpdateSsoConfig,
} from "controller";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { CreateSso } from "./create-sso";
import { SsoList } from "./SsoList";
import { CopySsoRedirectUrl } from "./copy-sso-redirect";
import { HowToConfigureBtn } from "./HowToConfigureBtn";
import { debugConsole } from "utilz/helpers";

export type SsoType = "OIDC" | "SAML";

export interface SsoFormValues {
  type: SsoType;
  domain: string;
  clientId: string;
  issuer: string;
  expiry_date: string;
  spEntityId: string;
  ssoConnectorId: string;

  // OIDC specific fields
  clientSecret: string;

  // SAML specific fields
  metadataType: "url" | "xml" | "unselected";
  metadataUrl: string;
  metadata: string;
}

const schema = yup.object({
  type: yup.string<SsoType>().required("Please select SSO type"),
  domain: yup
    .string()
    .matches(
      /^(?:(?:https?|ftp):\/\/)?[A-Za-z0-9-]{1,63}(\.[A-Za-z0-9-]{1,63})*\.[A-Za-z]{2,}$/,
      { message: "Please enter a valid domain" },
    ),
  ssoConnectorId: yup.string().nullable().optional(),

  // OIDC specific
  clientSecret: yup.string().when("type", {
    is: "OIDC",
    then: (schema) => schema.required("Client Secret is required for OIDC"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  clientId: yup.string().when("type", {
    is: "OIDC",
    then: (schema) => schema.required("Client ID is required for OIDC"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  issuer: yup.string().when("type", {
    is: "OIDC",
    then: (schema) =>
      schema
        .url("Please enter a valid URL")
        .required("OpenID Connect metadata document URL is required for OIDC"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  expiry_date: yup.string().when("type", {
    is: "OIDC",
    then: (schema) => schema.optional(), // Optional for OIDC
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  spEntityId: yup.string().when("type", {
    is: "OIDC",
    then: (schema) => schema.optional(), // Optional for OIDC
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  // publicCertificate: yup.string(),
  // acsUrl: yup.string().nullable().optional(),

  // SAML specific
  metadataType: yup.string<"url" | "xml" | "unselected">().when("type", {
    is: "SAML",
    then: (schema) => schema.required("Metadata type is required for SAML"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  metadataUrl: yup.string().when("type", {
    is: "SAML",
    then: (schema) =>
      schema.when("metadataType", {
        is: "url",
        then: (innerSchema) =>
          innerSchema
            .url("Please enter a valid URL")
            .required("Metadata URL is required for SAML"),
        otherwise: (innerSchema) => innerSchema.notRequired().nullable(),
      }),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  metadata: yup.string().when("type", {
    is: "SAML",
    then: (schema) =>
      schema.when("metadataType", {
        is: "xml",
        then: (innerSchema) =>
          innerSchema.required("XML metadata file content is required"),
        otherwise: (innerSchema) => innerSchema.notRequired().nullable(),
      }),
    otherwise: (schema) => schema.notRequired().nullable(), // Not required for OIDC
  }),
});

function SsoAddDrawer({
  onClose,
  drawerData,
  ssoDetails,
  isSsoDetailsLoading,
  customerId,
  isMspOnboarding,
  onSwitchToManualAdd,
  forWhom = "customers",
}: {
  onClose: () => void;
  drawerData?: any;
  ssoDetails: any;
  isSsoDetailsLoading: boolean;
  customerId?: string;
  isMspOnboarding?: boolean;
  onSwitchToManualAdd?: () => void;
  forWhom?: "customers" | "resources";
}) {
  const theme = useTheme();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [primaryButtonText, setPrimaryButtonText] = useState("Next");

  const [ssoListShow, setSsoListShow] = useState(true);
  const [ssoStateDetails, setSsoStateDetails] = useState<any>([]);
  const [isShowCopySsoRedirectUrl, setIsShowCopySsoRedirectUrl] =
    useState(false);
  const [currentCreatedSsoDomainName, setCurrentCreatedSsoDomainName] =
    useState<string>("");

  const { mutateAsync: createSso, isLoading: isCreatingSsoLoading } =
    useCreateSsoConfig();
  const { mutateAsync: updateSso, isLoading: isUpdateSsoLoading } =
    useUpdateSsoConfig();
  const { mutateAsync: deleteSso, isLoading: isDeletingSso } =
    useDeleteSsoConfig();

  useEffect(() => {
    if (ssoDetails?.length > 0) {
      setSsoStateDetails(ssoDetails);

      if (drawerData?.mode === "edit") {
        setSsoListShow(false);
        return;
      }

      const currentConnectorId = getValues("ssoConnectorId");
      const currentType = getValues("type");
      const hasMetadata = getValues("metadataUrl") || getValues("metadata");
      if (currentConnectorId && currentType === "SAML" && !hasMetadata) {
        setSsoListShow(false);
      } else {
        setSsoListShow(true);
      }
    } else {
      setSsoStateDetails([]);
      setPrimaryButtonText("Next");
      setSsoListShow(false);
    }
  }, [ssoDetails, isDrawerOpen, drawerData?.mode]);

  const initialFormFieldValues: SsoFormValues = {
    type: forWhom === "resources" ? "SAML" : "OIDC",
    domain: "",
    ssoConnectorId: "",

    // OIDC specific
    clientId: "",
    issuer: "",
    expiry_date: "",
    spEntityId: "",
    clientSecret: "",

    // SAML specific
    metadataType: "unselected",
    metadataUrl: "",
    metadata: "",
  };

  const methods = useForm({
    defaultValues: initialFormFieldValues,
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty },
    getValues,
    setError,
    clearErrors,
  } = methods;

  const handlePrimaryBtnClick = handleSubmit(
    async (values: SsoFormValues) => {
      const currentConnectorId = values.ssoConnectorId;
      const existingSso = ssoDetails?.find(
        (sso: any) =>
          sso.domain_name === values.domain &&
          sso.logto_connector_id !== currentConnectorId,
      );
      if (existingSso) {
        setError("domain", {
          type: "manual",
          message: "This domain is already configured for another SSO",
        });
        return;
      }
      clearErrors("domain");

      const originalSso = ssoDetails?.find(
        (sso: any) => sso.logto_connector_id === currentConnectorId,
      );
      const originalSsoType = originalSso?.type?.toUpperCase();
      const isSwitchingFromSamlToOidc =
        currentConnectorId &&
        originalSsoType === "SAML" &&
        values.type === "OIDC";

      if (isSwitchingFromSamlToOidc) {
        try {
          await deleteSso(currentConnectorId);
        } catch (error) {
          toast.error("Failed to delete existing SAML SSO");
          return;
        }
        values.ssoConnectorId = "";
        setValue("ssoConnectorId", "");
      }

      const payload: any = {
        is_resource_sso: forWhom === "resources",
        domains: [values.domain],
        syncProfile: true,
        providerName: values.type,
        connectorName: customerId || "",
        customer_id: customerId || "",
        config: {},
      };
      if (values.type === "OIDC") {
        payload.config.clientId = values.clientId;
        payload.config.issuer = values.issuer;
        payload.config.clientSecret = values.clientSecret;
        if (values.expiry_date) {
          payload.expiry_date = values.expiry_date;
        }
        if (values.spEntityId) {
          payload.spEntityId = values.spEntityId;
        }
      } else if (values.type === "SAML") {
        if (values.metadataType === "url") {
          payload.config.metadataUrl = values.metadataUrl;
        } else if (values.metadataType === "xml") {
          payload.config.metadata = values.metadata;
        }
      }
      if (Object.keys(payload.config).length === 0) {
        delete payload.config;
      }
      if (!values.ssoConnectorId) {
        try {
          setCurrentCreatedSsoDomainName("");
          const createSsoRes = await createSso(payload);
          setValue("ssoConnectorId", createSsoRes?.data?.logto_connector_id);
          if (values.type === "OIDC") {
            setIsShowCopySsoRedirectUrl(true);
            setSsoListShow(true);
          }
          setCurrentCreatedSsoDomainName(values.domain);
          toast.success("SSO created successfully");
          // reset(initialFormFieldValues);
          // if (isMspOnboarding) {
          //   onClose();
          // }
        } catch (error: any) {
          toast.error(
            error?.message ||
              "Unable to create SSO at the moment. Please try again later",
          );
        }
      } else {
        try {
          setCurrentCreatedSsoDomainName("");
          await updateSso({
            ssoConnectorId: values.ssoConnectorId || "",
            ...payload,
          });
          setSsoListShow(true);
          // setIsShowCopySsoRedirectUrl(true);
          setIsShowCopySsoRedirectUrl(false);
          toast.success("SSO updated successfully");
          reset(initialFormFieldValues);
          // if (isMspOnboarding) {
          //   onClose();
          // }
        } catch (error: any) {
          toast.error(
            error?.message ||
              "Unable to update SSO at the moment. Please try again later",
          );
        }
      }
    },
    (error) => {
      debugConsole("sso form submit error", error);
    },
  );

  useEffect(() => {
    if (drawerData?.mode === "edit" && drawerData?.data) {
      setPrimaryButtonText("Update");
      setSsoListShow(false);
      const data = drawerData.data;
      reset({
        ssoConnectorId: data?.logto_connector_id || "",
        type: data?.type?.toUpperCase() || "OIDC",
        domain: data?.domain_name || "",
        ...(data?.type?.toUpperCase() === "OIDC"
          ? {
              clientId: data?.open_id || "",
              issuer: data?.application_id || "",
              clientSecret: data?.secret_value || "",
              expiry_date: data?.expiry_date || "",
              spEntityId: data?.secret_id || "",
            }
          : data?.type?.toUpperCase() === "SAML"
            ? {
                metadataType: data?.saml_metadata
                  ? "xml"
                  : data?.saml_metadata_url
                    ? "url"
                    : "unselected",
                ...(data?.saml_metadata
                  ? {
                      metadata: data?.saml_metadata,
                    }
                  : data?.saml_metadata_url
                    ? {
                        metadataUrl: data?.saml_metadata_url,
                      }
                    : {}),
              }
            : {}),
      });
    } else if (drawerData?.mode === "create") {
      setPrimaryButtonText("Next");
    }
    if (drawerData?.open) {
      setDrawerOpen(true);
    } else {
      setDrawerOpen(false);
      reset(initialFormFieldValues);
    }
  }, [drawerData?.open, drawerData?.mode, drawerData?.data]);

  const onSecondaryBtnClick = async () => {
    const connectorId = getValues("ssoConnectorId");
    const currentType = getValues("type");
    const metadataUrl = getValues("metadataUrl");
    const metadata = getValues("metadata");
    const shouldDelete =
      connectorId && currentType === "SAML" && !metadataUrl && !metadata;

    reset(initialFormFieldValues);
    setCurrentCreatedSsoDomainName("");
    setIsShowCopySsoRedirectUrl(false);
    onClose();

    if (shouldDelete) {
      try {
        await deleteSso(connectorId);
      } catch (error) {
        toast.error(
          "Failed to clean up incomplete SAML SSO. You may need to delete it manually.",
        );
      }
    }
  };

  return (
    <>
      <Drawer
        title={"SSO Integration"}
        drawerStyle={{
          width: "680px",
          bgcolor: theme.palette.neutral.main,
          gap: "12px",
        }}
        headerTitleStyleSx={{ marginLeft: "10px" }}
        open={isDrawerOpen}
        onClose={onSecondaryBtnClick}
        onOpen={() => {}}
        secondaryButtonText={isShowCopySsoRedirectUrl ? "Close" : "Cancel"}
        onSecondaryButtonClick={onSecondaryBtnClick}
        primaryButtonText={primaryButtonText}
        isPrimaryButtonDisabled={!isDirty}
        primaryButtonSx={{
          display:
            isShowCopySsoRedirectUrl && getValues("type") !== "SAML"
              ? "none"
              : "flex",
        }}
        isLoading={isUpdateSsoLoading || isCreatingSsoLoading || isDeletingSso}
        onPrimaryButtonClick={handlePrimaryBtnClick}
        footer={
          !isShowCopySsoRedirectUrl && !isMspOnboarding && ssoListShow && <></>
        }
        headerRightActionComponent={
          !isShowCopySsoRedirectUrl && !isMspOnboarding && ssoListShow ? (
            <Button
              variant="secondary"
              endIcon={<PlusIcon endIconSize={"24px"} />}
              size="small"
              onClick={() => {
                setSsoListShow(!ssoListShow);
                setPrimaryButtonText("Next");
              }}
            >
              Add new
            </Button>
          ) : (
            <HowToConfigureBtn />
          )
        }
        footerLeftActionComponent={
          isMspOnboarding ? (
            <Button variant="ghost" onClick={onSwitchToManualAdd}>
              Would You Like To Add Users Manually?
            </Button>
          ) : (
            <></>
          )
        }
      >
        <FormProvider {...methods}>
          {isShowCopySsoRedirectUrl ? (
            <Box
              p="8px"
              minHeight={"100%"}
              bgcolor={theme.palette.neutral["005"]}
            >
              <CopySsoRedirectUrl
                logtoConnectorId={getValues("ssoConnectorId") || ""}
                domainName={currentCreatedSsoDomainName}
                ssoDetails={ssoDetails}
              />
            </Box>
          ) : isMspOnboarding ? (
            <CreateSso
              primaryButtonText={primaryButtonText}
              forWhom={forWhom}
              ssoDetails={ssoDetails}
              customerId={customerId}
            />
          ) : (
            <>
              <>
                {!ssoListShow && (
                  <CreateSso
                    primaryButtonText={primaryButtonText}
                    forWhom={forWhom}
                    ssoDetails={ssoDetails}
                    customerId={customerId}
                  />
                )}
              </>
              <>
                {ssoListShow && (
                  <SsoList
                    ssoStateDetails={ssoStateDetails}
                    isUpdateSsoLoading={isUpdateSsoLoading}
                    setSsoListShow={setSsoListShow}
                    setPrimaryButtonText={setPrimaryButtonText}
                  />
                )}
              </>
            </>
          )}
        </FormProvider>
      </Drawer>
    </>
  );
}

export { SsoAddDrawer };
